import { useState } from "react";
import { m } from "framer-motion";
import { Lock, CheckCircle2, Sparkles, Send } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { licensingApi } from "../api/licensingApi";
import { toast } from "../components/common/toast";

interface ModuleDisabledProps {
  /** Which module to pitch. */
  moduleKey?: "contract" | "obligation" | "receivable";
  /** Set when the organisation's plan leaves the module out — the copy
   *  points at the subscription rather than at an admin switch. */
  planModuleKey?: string;
}

/**
 * The page behind any route whose module the organisation has not
 * subscribed to. It reads as a product page for that module — what it is,
 * what it does for the reader, and the upgrade that unlocks it — rather
 * than as an error, since the reader arrived here by choosing the feature.
 */
export function ModuleDisabled({ moduleKey, planModuleKey }: ModuleDisabledProps) {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = !!(user?.isAdmin || user?.isAppAdmin);
  const [requestState, setRequestState] = useState<"idle" | "sending" | "sent">("idle");

  const feature = moduleKey
    ? t(`moduleDisabledPitch.${moduleKey}.title`)
    : t("moduleDisabled.genericFeature", { defaultValue: "this module" });
  const benefits = moduleKey
    ? (t(`moduleDisabledPitch.${moduleKey}.benefits`, { returnObjects: true }) as unknown)
    : [];
  const benefitList = Array.isArray(benefits) ? (benefits as string[]) : [];

  return (
    <m.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex flex-col items-center rounded-2xl border border-[rgb(var(--border))] bg-[rgb(var(--card))] px-6 py-10 text-center shadow-lifted"
    >
      <div className="relative mb-5">
        <div className="absolute inset-0 rounded-full bg-[rgb(var(--accent-fill))] opacity-10 blur-xl animate-pulse" />
        <div className="relative rounded-full border border-[rgb(var(--border))] bg-[rgb(var(--card-muted))] p-5 text-[rgb(var(--text-accent))]">
          <Lock size={40} />
        </div>
      </div>

      <h2 className="mb-2 text-2xl font-bold text-[rgb(var(--text-primary))]">
        {planModuleKey
          ? t("moduleDisabled.planTitle", {
              defaultValue: "{{feature}} is not in your current plan",
              feature,
            })
          : t("titleModuleDisabled")}
      </h2>
      <p className="max-w-xl leading-relaxed text-[rgb(var(--text-muted))]">
        {planModuleKey
          ? t("moduleDisabled.planDescription", {
              defaultValue:
                "{{feature}} is not subscribed by your organisation in the current subscription plan. Upgrade your subscription to use {{feature}}.",
              feature,
            })
          : t("descriptionModuleDisabled")}
      </p>

      {moduleKey && (
        <div className="mt-8 w-full max-w-2xl rounded-2xl border border-[rgb(var(--text-accent))]/30 bg-gradient-to-br from-[rgb(var(--text-accent))]/10 to-transparent p-6 text-left">
          <div className="flex items-center gap-2 text-[rgb(var(--text-accent))]">
            <Sparkles size={18} />
            <span className="text-lg font-semibold text-[rgb(var(--text-primary))]">
              {t(`moduleDisabledPitch.${moduleKey}.headline`, {
                defaultValue: feature,
              })}
            </span>
          </div>
          <p className="mt-2 text-sm leading-relaxed text-[rgb(var(--text-secondary))]">
            {t(`moduleDisabledPitch.${moduleKey}.body`)}
          </p>
          {benefitList.length > 0 && (
            <ul className="mt-4 grid gap-2 sm:grid-cols-2">
              {benefitList.map((b) => (
                <li
                  key={b}
                  className="flex items-start gap-2 rounded-lg bg-[rgb(var(--card))] px-3 py-2 text-sm text-[rgb(var(--text-primary))]"
                >
                  <CheckCircle2
                    size={16}
                    className="mt-0.5 shrink-0 text-[rgb(var(--text-accent))]"
                  />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {planModuleKey && canManage ? (
        <button
          type="button"
          onClick={() => navigate("/admin/subscription")}
          className="mt-8 rounded-lg bg-[rgb(var(--accent-fill))] px-6 py-2.5 text-sm font-semibold text-[rgb(var(--accent-ink))] shadow-sm hover:opacity-90"
        >
          {t("moduleDisabled.upgradeCta", {
            defaultValue: "Upgrade subscription to use {{feature}}",
            feature,
          })}
        </button>
      ) : planModuleKey ? (
        // Someone who cannot change the plan can still ask for it: one
        // click sends the admins an email and a notification naming this
        // member, the organisation and the module they wanted.
        <div className="mt-8 flex flex-col items-center gap-2">
          <button
            type="button"
            disabled={requestState !== "idle"}
            onClick={async () => {
              setRequestState("sending");
              try {
                await licensingApi.requestUpgrade({
                  moduleKey: planModuleKey,
                  moduleName: feature,
                });
                setRequestState("sent");
                toast.success(
                  t("moduleDisabled.requestSent", {
                    defaultValue:
                      "Your organisation's admin has been asked to upgrade the subscription for {{feature}}.",
                    feature,
                  }),
                );
              } catch {
                setRequestState("idle");
                toast.error(
                  t("moduleDisabled.requestFailed", {
                    defaultValue: "Could not send the request. Please try again.",
                  }),
                );
              }
            }}
            className="inline-flex items-center gap-2 rounded-lg bg-[rgb(var(--accent-fill))] px-6 py-2.5 text-sm font-semibold text-[rgb(var(--accent-ink))] shadow-sm hover:opacity-90 disabled:opacity-60"
          >
            <Send size={15} />
            {requestState === "sent"
              ? t("moduleDisabled.requestSentShort", { defaultValue: "Request sent" })
              : t("moduleDisabled.contactAdmin", {
                  defaultValue: "Contact your organisation's admin to upgrade",
                })}
          </button>
          <p className="max-w-md text-xs text-[rgb(var(--text-muted))]">
            {t("moduleDisabled.contactAdminHint", {
              defaultValue:
                "Your admin will receive an email and a notification asking them to add {{feature}}.",
              feature,
            })}
          </p>
        </div>
      ) : (
        <p className="mt-8 max-w-md text-sm font-medium text-[rgb(var(--text-accent))]">
          {t("moduleDisabledCta")}
        </p>
      )}
    </m.div>
  );
}
